import { Info, type StringUnitLength } from "luxon";
import { DAYS_PER_WEEK } from "../../../../utils/models";
import { WEEKDAYS } from "../../../Settings/DepartmentConfiguration/utils";

/** A weekday as the editor stores it: `WEEKDAYS`' short lower-case key. */
export type WeekdayKey = (typeof WEEKDAYS)[number];

/** One weekday of a v2 day picker, in the order the week is drawn, with its visible and spoken names. */
export type WeekdayChip = { day: WeekdayKey; label: string; name: string };

/** The longest a chip label may run before the picker falls back to single-letter labels. */
const MAX_CHIP_LABEL_LENGTH = 3;

/**
 * Every value must be one of the stored weekday keys ("sun" … "sat"). Anything else is data the page
 * can't place on a week, so it throws rather than quietly dropping (or misnaming) the day.
 */
export function assertWeekdayKeys(days: readonly string[]): asserts days is readonly WeekdayKey[] {
	const unknown = days.filter((day) => !WEEKDAYS.some((weekday) => weekday === day));
	if (unknown.length > 0) throw new Error(`assertWeekdayKeys: unknown weekday keys ${JSON.stringify(unknown)}`);
}

/** `WEEKDAYS` (Sunday first) rotated to start on `weekStartDay` (0 = Sunday … 6 = Saturday), as the v1 day pills order it. */
export const weekdaysFrom = (weekStartDay: number): WeekdayKey[] => {
	const offset = ((weekStartDay % DAYS_PER_WEEK) + DAYS_PER_WEEK) % DAYS_PER_WEEK;
	return [...WEEKDAYS.slice(offset), ...WEEKDAYS.slice(0, offset)];
};

/** `days` in the order the department's week is drawn. Throws on a value that isn't a stored weekday key. */
export const inWeekOrder = (days: readonly string[], weekStartDay: number): WeekdayKey[] => {
	assertWeekdayKeys(days);
	return weekdaysFrom(weekStartDay).filter((weekday) => days.includes(weekday));
};

/**
 * A stored weekday's name in the app's locale (Luxon's locale follows the i18n language): "mon" is "Mon" short,
 * "M" narrow, "Monday" long. The one source of weekday names for the location editor and the Locations page.
 * Throws on a value that isn't a stored weekday key.
 */
export const weekdayName = (day: string, width: StringUnitLength): string => {
	const days = [day];
	assertWeekdayKeys(days);
	// `Info` lists are Monday-first; `WEEKDAYS` is Sunday-first, so Sunday (0 there) is the last of a Monday-first week.
	return Info.weekdays(width)[(WEEKDAYS.indexOf(days[0]) + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK];
};

/** "Mon, Wed, Fri": `days`' chip labels (narrow where a locale's short names are words, as in Hebrew), in the department's week order. Throws on a value that isn't a stored weekday key. */
export const weekdayListLabel = (days: readonly string[], weekStartDay: number): string =>
	inWeekOrder(days, weekStartDay)
		.map((day) => weekdayChipLabel(day))
		.join(", ");

/**
 * The week's chips in the app's locale. The label is the short name ("Mon") where every short name fits a
 * chip, and the narrow one ("M") where a locale's short names don't (Hebrew's are words); the full name is
 * the chip's accessible name either way.
 */
export const weekdayChips = (weekStartDay: number): WeekdayChip[] => {
	const labelWidth = chipLabelWidth();
	return weekdaysFrom(weekStartDay).map((day) => ({ day, label: weekdayName(day, labelWidth), name: weekdayName(day, "long") }));
};

/**
 * The week's chips with one-letter glyphs ("M"), for the 20px round chips of a dense line (the v2 staffing lines, as
 * the rows draw them); the full name is still each chip's accessible name.
 */
export const narrowWeekdayChips = (weekStartDay: number): WeekdayChip[] =>
	weekdaysFrom(weekStartDay).map((day) => ({ day, label: weekdayName(day, "narrow"), name: weekdayName(day, "long") }));

/** The short names where every one fits a chip ("Mon", "lun"), else the narrow ones (Hebrew's short names are words: "ב׳", not "יום ב׳"). */
const chipLabelWidth = (): StringUnitLength => (Info.weekdays("short").every((name) => name.length <= MAX_CHIP_LABEL_LENGTH) ? "short" : "narrow");

/** A weekday as a chip shows it, the same label the day pickers draw. Throws on a value that isn't a stored weekday key. */
export const weekdayChipLabel = (day: string): string => weekdayName(day, chipLabelWidth());

/** `days` with `day` switched on or off, in week order. */
export const toggleWeekday = (days: readonly string[], day: WeekdayKey): WeekdayKey[] => {
	const next = new Set(days);
	if (next.has(day)) {
		next.delete(day);
	} else {
		next.add(day);
	}
	return WEEKDAYS.filter((weekday) => next.has(weekday));
};
